type CalendarEmployeeId = number | string | null | undefined;
type CalendarEmployeeResource = { id?: CalendarEmployeeId };

export type LogbookBookingAccessPayload = {
    employeeId?: CalendarEmployeeId;
    resourceId?: CalendarEmployeeId;
    resource?: CalendarEmployeeId | { id?: CalendarEmployeeId } | null;
    responseObject?: {
        employeeId?: CalendarEmployeeId;
        employee?: {
            id?: CalendarEmployeeId;
        } | null;
    } | null;
};

export type LogbookBookingAccess = {
    canOpenPopover?: boolean;
    canEditBooking?: boolean;
    canEditStatus?: boolean;
    canWriteOff?: boolean;
};

export type LogbookAccess = {
    canCreateForEmployee?: (employeeId?: number | null) => boolean;
    canCreateScheduleForEmployee?: (employeeId?: number | null) => boolean;
    canCreateCustomDayForEmployee?: (employeeId?: number | null) => boolean;
    canCreateVacationForEmployee?: (employeeId?: number | null) => boolean;
    canOpenEmployeeServicesForEmployee?: (employeeId?: number | null) => boolean;
    resolveBookingAccess?: (booking: LogbookBookingAccessPayload) => LogbookBookingAccess;
};

type BuildLogbookCalendarAccessParams = {
    currentBranchEmployeeId: number | null;
    canCreateLogbookBookingAll: boolean;
    canCreateLogbookBookingSelf: boolean;
    canEditLogbookBookingAll: boolean;
    canEditLogbookBookingSelf: boolean;
    canEditLogbookPopoverBookingStatusAll: boolean;
    canEditLogbookPopoverBookingStatusSelf: boolean;
    canWriteOffLogbookConsumables: boolean;
    canCreateScheduleAll: boolean;
    canCreateScheduleSelf: boolean;
    canCreateCustomDayAll: boolean;
    canCreateCustomDaySelf: boolean;
    canCreateVacationAll: boolean;
    canCreateVacationSelf: boolean;
    canOpenEmployeeServicesAll: boolean;
    canOpenEmployeeServicesSelf: boolean;
};

const normalizeEmployeeId = (employeeId: CalendarEmployeeId): number | null => {
    if (employeeId == null || employeeId === "") {
        return null;
    }

    const normalizedEmployeeId = Number(employeeId);

    return Number.isFinite(normalizedEmployeeId) ? normalizedEmployeeId : null;
};

const isCalendarEmployeeResource = (
    resource: LogbookBookingAccessPayload["resource"]
): resource is CalendarEmployeeResource => resource != null && typeof resource === "object";

export const resolveEmployeeScopedAccess = (
    employeeId: CalendarEmployeeId,
    currentBranchEmployeeId: number | null,
    canAll: boolean,
    canSelf: boolean
): boolean => {
    const normalizedEmployeeId = normalizeEmployeeId(employeeId);

    if (!normalizedEmployeeId) {
        return false;
    }

    return normalizedEmployeeId === currentBranchEmployeeId ? canSelf || canAll : canAll;
};

export const normalizeBookingEmployeeId = (booking: LogbookBookingAccessPayload): number | null => {
    const resource = booking?.resource;
    const resourceEmployeeId: CalendarEmployeeId = isCalendarEmployeeResource(resource) ? resource.id : resource;

    return normalizeEmployeeId(
        booking?.employeeId ??
            booking?.responseObject?.employeeId ??
            booking?.responseObject?.employee?.id ??
            resourceEmployeeId ??
            booking?.resourceId
    );
};

export const buildLogbookCalendarAccess = ({
    currentBranchEmployeeId,
    canCreateLogbookBookingAll,
    canCreateLogbookBookingSelf,
    canEditLogbookBookingAll,
    canEditLogbookBookingSelf,
    canEditLogbookPopoverBookingStatusAll,
    canEditLogbookPopoverBookingStatusSelf,
    canWriteOffLogbookConsumables,
    canCreateScheduleAll,
    canCreateScheduleSelf,
    canCreateCustomDayAll,
    canCreateCustomDaySelf,
    canCreateVacationAll,
    canCreateVacationSelf,
    canOpenEmployeeServicesAll,
    canOpenEmployeeServicesSelf,
}: BuildLogbookCalendarAccessParams): LogbookAccess => ({
    canCreateForEmployee: (employeeId) =>
        resolveEmployeeScopedAccess(
            employeeId,
            currentBranchEmployeeId,
            canCreateLogbookBookingAll,
            canCreateLogbookBookingSelf
        ),
    canCreateScheduleForEmployee: (employeeId) =>
        resolveEmployeeScopedAccess(employeeId, currentBranchEmployeeId, canCreateScheduleAll, canCreateScheduleSelf),
    canCreateCustomDayForEmployee: (employeeId) =>
        resolveEmployeeScopedAccess(employeeId, currentBranchEmployeeId, canCreateCustomDayAll, canCreateCustomDaySelf),
    canCreateVacationForEmployee: (employeeId) =>
        resolveEmployeeScopedAccess(employeeId, currentBranchEmployeeId, canCreateVacationAll, canCreateVacationSelf),
    canOpenEmployeeServicesForEmployee: (employeeId) =>
        resolveEmployeeScopedAccess(
            employeeId,
            currentBranchEmployeeId,
            canOpenEmployeeServicesAll,
            canOpenEmployeeServicesSelf
        ),
    resolveBookingAccess: (booking) => {
        const bookingEmployeeId = normalizeBookingEmployeeId(booking);

        return {
            canOpenPopover: true,
            canEditBooking: resolveEmployeeScopedAccess(
                bookingEmployeeId,
                currentBranchEmployeeId,
                canEditLogbookBookingAll,
                canEditLogbookBookingSelf
            ),
            canEditStatus: resolveEmployeeScopedAccess(
                bookingEmployeeId,
                currentBranchEmployeeId,
                canEditLogbookPopoverBookingStatusAll,
                canEditLogbookPopoverBookingStatusSelf
            ),
            canWriteOff: canWriteOffLogbookConsumables,
        };
    },
});
